import type { ReactElement, ReactNode } from 'react';
import { PublicationStatus, type PublicationDto } from '@gr/core/distribution/publishing/dto';
import { useT } from '../../../../app/di';
import { formatDateTime } from '../../../../shared/format';
import { Badge, type Tone } from '../../../../ui-kit/Badge/Badge';
import styles from './PublicationLine.module.scss';

const TONE: Record<PublicationStatus, Tone> = {
  [PublicationStatus.Planned]: 'accent',
  [PublicationStatus.Due]: 'warning',
  [PublicationStatus.Published]: 'success',
  [PublicationStatus.Cancelled]: 'neutral',
};

export function PublicationStatusBadge({ status }: { status: PublicationStatus }): ReactElement {
  const { t } = useT();
  return <Badge tone={TONE[status]}>{t(`scheduling.status.${status}`)}</Badge>;
}

export interface PublicationLineProps {
  publication: PublicationDto;
  /** Пояс блога: время публикации показывается в нём. */
  timezone: string;
  actions?: ReactNode;
}

/** Строка публикации: канал, время в поясе блога, статус. */
export function PublicationLine({ publication, timezone, actions }: PublicationLineProps): ReactElement {
  const { t, locale } = useT();
  const time = publication.status === PublicationStatus.Published ? publication.publishedAt : publication.plannedAt;
  return (
    <div className={styles.line} data-testid="publication-line">
      <div className={styles.info}>
        <strong className={styles.channel}>{publication.channelName}</strong>
        <span className={time ? '' : 'u-muted'}>{time ? formatDateTime(time, locale, timezone) : t('scheduling.noTime')}</span>
        <PublicationStatusBadge status={publication.status} />
        {publication.pinned && publication.status === PublicationStatus.Planned ? <Badge>{t('scheduling.pinned')}</Badge> : null}
      </div>
      {actions ? <div className="u-row">{actions}</div> : null}
    </div>
  );
}
